<template>
    <div class="segment-dependents-list">
        <div
            v-if="dependentsLoading"
            class="dependents-loading">
            <pendo-loading-indicator
                size="small"
                :loading="dependentsLoading" />
            <span class="dependents-loading-text">{{ $t('segmentDependencyList.Checking for dependencies...') }}</span>
        </div>
        <div v-else>
            <pendo-alert
                v-if="dependentsExist"
                class="delete-warning"
                type="warning">
                <div>
                    {{ deleteWarning }}
                </div>
            </pendo-alert>
            <div
                v-for="segment in segments"
                :key="segment.id"
                class="segment-dependents">
                <div
                    v-pendo-tooltip="
                        hasDeleteBlockingDependents(segment) && {
                            placement: 'top',
                            content: segmentDependentsTooltipContent,
                            multiline: true,
                            boundariesElement: 'viewport',
                        }
                    ">
                    <pendo-checkbox
                        v-if="multipleSegments"
                        class="segment-name"
                        :disabled="hasDeleteBlockingDependents(segment)"
                        :value="!!selectedSegmentsMap[segment.id]"
                        :label="segment.name"
                        @change="handleSelectChange(segment)" />
                </div>
                <div
                    v-if="segmentHasDependents(segment)"
                    class="dependency-sections">
                    <div
                        v-if="hasDependentSegments(segment)"
                        class="dependent-type-group dependentSegments"
                        :class="{ 'multiple-segments': multipleSegments }">
                        <label class="dependent-list-label">{{
                            $t('segmentDependencyList.Other Segments ({count})', {
                                count: getTotalDependentSegmentsCount(segment),
                            })
                        }}</label>
                        <ul class="dependent-list">
                            <li
                                v-for="dependent in segmentDependents[segment.id]['dependentSegments']"
                                :key="dependent.id"
                                class="dependent-list-item dependent-segment">
                                <pendo-router-link
                                    class="dependent-link"
                                    :target="openSegmentsInNewTab ? '_blank' : undefined"
                                    :to="generateSegmentLink(dependent.id)"
                                    @click="onSegmentLinkClick(dependent)">
                                    {{ dependent.name }}
                                </pendo-router-link>
                            </li>
                            <li
                                v-for="{ report, draftSegment } in segmentDependents[segment.id][
                                    'dependentDraftSegments'
                                ]"
                                :key="`${report.id}-${draftSegment.name}`"
                                class="dependent-list-item dependent-draft-segment">
                                <i18n-t
                                    keypath="segmentDependencyList.draftSegmentDependency.mainMessage"
                                    tag="span">
                                    <template #segmentName>
                                        <pendo-router-link
                                            class="dependent-link"
                                            target="_blank"
                                            :to="{
                                                name: 'DataExplorer',
                                                params: { id: report.id },
                                                query: { draftSegment: draftSegment.name },
                                            }">
                                            {{ draftSegment.name }}
                                        </pendo-router-link>
                                    </template>
                                    <template #reportName>
                                        <pendo-router-link
                                            class="dependent-link"
                                            target="_blank"
                                            :to="{ name: 'DataExplorer', params: { id: report.id } }">
                                            {{ report.name }}
                                        </pendo-router-link>
                                    </template>
                                </i18n-t>
                            </li>
                            <li
                                v-for="entity in segmentDependents[segment.id]['dependentCustomSegments']"
                                :key="entity.id"
                                class="dependent-list-item dependent-custom-segment">
                                {{ customSegmentType(entity) }}
                                <pendo-router-link
                                    class="dependent-link"
                                    target="_blank"
                                    :to="entity.routeObj">
                                    {{ entity.name }}
                                </pendo-router-link>
                            </li>
                        </ul>
                    </div>
                    <div
                        v-for="(dependentType, dependentKey) in nonSegmentDependentTypes"
                        :key="dependentKey"
                        :class="dependentKey">
                        <div
                            v-if="hasDependentsOfType(segment, dependentKey)"
                            class="dependent-type-group"
                            :class="{ 'multiple-segments': multipleSegments }">
                            <label class="dependent-list-label">{{
                                getDependentTypeLabel(
                                    dependentType,
                                    getSegmentDependentsCountByKey(segment, dependentKey),
                                )
                            }}
                            </label>
                            <ul class="dependent-list">
                                <li
                                    v-for="dependent in segmentDependents[segment.id][dependentKey]"
                                    :key="dependent.id"
                                    class="dependent-list-item">
                                    <pendo-router-link
                                        class="dependent-link"
                                        :to="dependent.routeObj"
                                        target="_blank">
                                        {{ dependent.name }}
                                    </pendo-router-link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div
                    v-else
                    class="no-dependencies"
                    :class="{ 'multiple-segments': multipleSegments }">
                    {{ noDependentsMessage }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import { PendoAlert, PendoLoadingIndicator, PendoCheckbox, PendoTooltip } from '@pendo/components';
import PendoRouterLink from '@/vue-router/pendo-router-link.vue';

export default {
    name: 'SegmentDependencyList',
    components: {
        PendoAlert,
        PendoLoadingIndicator,
        PendoCheckbox,
        PendoRouterLink,
    },
    directives: {
        PendoTooltip,
    },
    props: {
        openSegmentsInNewTab: {
            type: Boolean,
            default: true,
        },
        dependentsLoading: {
            type: Boolean,
            default: false,
        },
        segments: {
            type: Array,
            required: true,
        },
        segmentDependents: {
            type: Object,
            required: true,
        },
    },
    emits: ['dependents-loaded', 'select-change', 'link-click'],
    data () {
        return {
            nonSegmentDependentTypes: {
                dependentGuides: 'Guides',
                dependentEmails: 'Emails',
                dependentJourneys: 'Journeys',
                dependentReports: 'Reports',
                dependentReplayApps: 'Replay',
            },
            selectedSegmentsMap: {},
        };
    },
    computed: {
        deleteWarning () {
            return this.$t(
                'segmentDependencyList.Segments that are referenced by guides, replay apps, emails, journeys, or other segments cannot be deleted. Segments referenced in reports can be deleted, but may produce unexpected results.',
            );
        },
        noDependentsMessage () {
            return this.$t(
                'segmentDependencyList.This segment is not used by any guides, replay apps, emails, journeys, or other segments.',
            );
        },
        segmentDependentsTooltipContent () {
            return this.$t(
                'segmentDependencyList.This segment cannot be deleted because it is currently referenced by a dependency.',
            );
        },
        dependentsExist () {
            return !isEmpty(this.segmentsWithDependents);
        },
        segmentsWithDependents () {
            const segmentsWithDependents = this.segments.reduce((acc, segment) => {
                const dependents = this.segmentDependents[segment.id];

                if (!dependents) return acc;

                const hasDependents = Object.keys(dependents).some((key) => {
                    const depArr = dependents[key];
                    if (!isArray(depArr)) return false;

                    return !!depArr.length;
                });

                if (!hasDependents) return acc;

                acc[segment.id] = this.segmentDependents[segment.id];

                return acc;
            }, {});

            return segmentsWithDependents;
        },
        multipleSegments () {
            return this.segments.length > 1;
        },
        selectedSegments () {
            return Object.values(this.selectedSegmentsMap);
        },
    },
    watch: {
        dependentsLoading: {
            handler (loading) {
                if (loading) return;

                this.buildSelectedSegmentsMap();
            },
        },
    },
    created () {
        this.buildSelectedSegmentsMap();
    },
    methods: {
        getSegmentDependentsCountByKey (segment, dependentKey) {
            return this.segmentDependents[segment.id][dependentKey].length;
        },
        getTotalDependentSegmentsCount (segment) {
            return (
                this.segmentDependents[segment.id].dependentSegments.length +
                this.segmentDependents[segment.id].dependentDraftSegments.length +
                this.segmentDependents[segment.id].dependentCustomSegments.length
            );
        },
        segmentHasDependents (segment) {
            return Object.keys(this.segmentDependents[segment.id]).some((key) => {
                if (!isArray(this.segmentDependents[segment.id][key])) {
                    return false;
                }

                return this.segmentDependents[segment.id][key].length > 0;
            });
        },
        handleSelectChange (segment) {
            const { id } = segment;
            if (this.selectedSegmentsMap[id]) {
                this.$delete(this.selectedSegmentsMap, id);
            } else {
                this.$set(this.selectedSegmentsMap, id, segment);
            }
            this.$emit('select-change', this.selectedSegments);
        },
        hasDependentsOfType (segment, dependentKey) {
            if (!this.segmentDependents[segment.id]) return false;

            return !!this.segmentDependents[segment.id][dependentKey]?.length;
        },
        hasDeleteBlockingDependents (segment) {
            const hasDependentSegments = this.hasDependentSegments(segment);
            const hasDependentGuides = this.hasDependentsOfType(segment, 'dependentGuides');
            const hasDependentReplayApps = this.hasDependentsOfType(segment, 'dependentReplayApps');
            const hasDependentEmails = this.hasDependentsOfType(segment, 'dependentEmails');
            const hasDependentJourneys = this.hasDependentsOfType(segment, 'dependentJourneys');

            const hasDependents =
                hasDependentSegments ||
                hasDependentGuides ||
                hasDependentReplayApps ||
                hasDependentEmails ||
                hasDependentJourneys;

            return hasDependents;
        },
        hasDependentSegments (segment) {
            return (
                this.hasDependentsOfType(segment, 'dependentSegments') ||
                this.hasDependentsOfType(segment, 'dependentDraftSegments') ||
                this.hasDependentsOfType(segment, 'dependentCustomSegments')
            );
        },
        generateSegmentLink (dependent) {
            if (!this.openSegmentsInNewTab) return null;

            return { name: 'Segments', query: { segment: dependent.id } };
        },
        onSegmentLinkClick (segment) {
            if (this.openSegmentsInNewTab) return;

            this.$emit('link-click', { name: 'Segments', query: { segment: segment.id } });
        },
        getDependentTypeLabel (dependentType, count) {
            switch (dependentType) {
                case 'Guides':
                    return this.$t('segmentDependencyList.Guides using this segment ({count})', { count });
                case 'Emails':
                    return this.$t('segmentDependencyList.Emails using this segment ({count})', { count });
                case 'Journeys':
                    return this.$t('segmentDependencyList.Journeys using this segment ({count})', { count });
                case 'Reports':
                    return this.$t('segmentDependencyList.Reports using this segment ({count})', { count });
                case 'Replay':
                    return this.$t('segmentDependencyList.Session Replay applications using this segment ({count})', {
                        count,
                    });
                default:
                    return dependentType;
            }
        },
        buildSelectedSegmentsMap () {
            if (this.dependentsLoading) {
                this.selectedSegmentsMap = {};

                return;
            }

            this.selectedSegmentsMap = this.segments.reduce((acc, segment) => {
                const hasDependents = this.hasDeleteBlockingDependents(segment);

                if (!hasDependents) {
                    acc[segment.id] = segment;
                }

                return acc;
            }, {});

            this.$emit('select-change', this.selectedSegments);
        },
        customSegmentType (entity) {
            switch (entity?.type) {
                case 'replay':
                    return this.$t('segmentDependencyList.Custom segment in replay app');
                case 'email':
                    return this.$t('segmentDependencyList.Custom segment in email');
                case 'journey':
                    return this.$t('segmentDependencyList.Custom segment in journey');
                default:
            }

            // generic fallback in case new type is introduced without updating this function
            if (entity?.type) {
                return this.$t('segmentDependencyList.Custom segment in {type}', { type: entity?.type });
            }

            // for whatever reason,
            // custom segments in guide don't have type defined
            return this.$t('segmentDependencyList.Custom segment in guide');
        },
    },
};
</script>

<style lang="less" scoped>
.segment-dependents-list {
    font-size: 14px;
}

.dependents-loading {
    display: flex;
    padding: 16px 0;
}

.dependents-loading-text {
    margin-left: 10px;
}

.delete-warning {
    margin-top: 16px;
}

.no-dependencies {
    color: var(--palette-gray-70);
    word-break: break-word;

    &.multiple-segments {
        padding-left: 35px;
    }
}

.segment-dependents {
    word-break: break-all;
    margin-top: 12px;
    display: flex;
    flex-direction: column;
}

.segment-name {
    margin: 0;

    &:first-child {
        padding-top: 0;
    }

    .pendo-checkbox__label {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    :deep(.pendo-checkbox) {
        font-weight: bold;
    }
}

.dependent-type-group {
    &:not(:first) {
        margin-top: 5px;
    }

    &.multiple-segments {
        padding-left: 35px;
    }
}

.dependent-list {
    padding: 0;
    margin: 0;
}

.dependent-list-label {
    color: var(--palette-gray-110);
    display: block;
    margin: 0;
}

.dependent-type-group:not(.multiple-segments):first-child .dependent-list-label {
    border-top: none;
    padding-top: 0;
}

.dependent-list-item {
    line-height: 23px;
    overflow-x: hidden;
    padding-left: 12px;
    text-overflow: ellipsis;
}
</style>
